import ApiService from '../apiService';

// Backend `streamChatTurn` aborts the LLM stream at 60s (TIMEOUT_MS). Give the
// browser a small skew so the backend's abort/error event can land before the
// client tears the connection down.
const STREAM_TIMEOUT_MS = 65000;

function getStreamBaseUrl() {
  return ApiService.instance.defaults.baseURL;
}

function createStreamAbortController(externalSignal, timeoutMs) {
  const abortController = new AbortController();
  const timer = setTimeout(() => abortController.abort('timeout'), timeoutMs);
  if (externalSignal) {
    if (externalSignal.aborted) {
      abortController.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener('abort', () => abortController.abort(externalSignal.reason), { once: true });
    }
  }
  return { abortController, timer };
}

function parseEventLine(line) {
  try {
    return JSON.parse(line);
  } catch (parseError) {
    return null;
  }
}

async function consumeNdjsonStream(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let isDone = false;

  while (!isDone) {
    const { value, done } = await reader.read();
    isDone = done;
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) {
        continue;
      }
      const event = parseEventLine(line);
      if (event) {
        await onEvent(event);
      }
    }
  }

  if (buffer.trim()) {
    const event = parseEventLine(buffer);
    if (event) {
      await onEvent(event);
    }
  }
}

export default {
  async parseErrorResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return {
        message: `HTTP_${response.status}`,
      };
    }
  },

  fetchModelProfiles() {
    return ApiService.instance.get('/assistant/model-profiles').then((response) => {
      return response.data;
    });
  },

  async chatStream(payload, { onEvent, signal, timeoutMs = STREAM_TIMEOUT_MS } = {}) {
    const { abortController, timer } = createStreamAbortController(signal, timeoutMs);

    try {
      const response = await fetch(`${getStreamBaseUrl()}/assistant/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const parsedError = await this.parseErrorResponse(response);
        const error = new Error(parsedError.message || `HTTP_${response.status}`);
        error.response = { status: response.status, data: parsedError };
        throw error;
      }

      if (!response.body) {
        const error = new Error('ASSISTANT_STREAM_NOT_AVAILABLE');
        error.response = { status: response.status, data: { message: 'ASSISTANT_STREAM_NOT_AVAILABLE' } };
        throw error;
      }

      await consumeNdjsonStream(response, onEvent);
    } finally {
      clearTimeout(timer);
    }
  },
};
